import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import RightNavSection from "./right-nav-section";
import MetaHead from "@/components/meta/head";
import { useEffect } from "react";
import OnInitHub from "@/services/signal-R/on-receive-hook";
import ThemeSidebar from "./theme-sidebar";
import PackagesModal from "./packages-modal";
import GetIcon from "@/components/font-awesome-icon";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import useFeatures from "@/hooks/useFeatures";
import { FeatureKeys } from "@/services/dropdown-enums";

const HEADER_HEIGHT = 66;

export default function AppNavBar(props) {
  const { initHub } = OnInitHub();
  const [open, setOpen] = React.useState(false);
  const [packagesModalOpen, setPackagesModalOpen] = React.useState(false);

  const profile = useSelector(getLoggedinUser);
  const { hasFeature } = useFeatures();
  const isBasicPlan = !hasFeature(FeatureKeys.CRM);

  // Compute days left from the ISO end date if it exists
  let daysLeft = null;
  if (profile?.planEndDate) {
    const endDate = new Date(profile.planEndDate);
    const now = new Date();
    const timeDiff = endDate.getTime() - now.getTime();
    daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  useEffect(() => {
    //subscribe all hub on response methods here
    initHub();
  }, []);

  const handleTheme = (e) => {
    setOpen(true);
  };

  return (
    <AppBar
      component="nav"
      sx={{
        backgroundColor: "var(--palette-header_bg)",
        height: HEADER_HEIGHT,
        paddingRight: "0 !important",
        zIndex: 1050, // below sidebar (1100) so sidebar overlaps if needed on edges
        borderRadius: "0px",
        border: "none",
        boxShadow: "none",
        left: 86, // offset by expanded sidebar width
        width: "calc(100% - 86px)",
      }}
      elevation={0}
    >
      <MetaHead />
      <Toolbar
        sx={{
          height: HEADER_HEIGHT,
          minHeight: `${HEADER_HEIGHT}px !important`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "34px !important",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            px: "40px",
          }}
        >
          {isBasicPlan && (
            <>
              <GetIcon
                icon={faBullhorn}
                iconStyles={{
                  color: "var(--palette-header_text, #fff)",
                  fontSize: "16px",
                }}
              />
              <Typography
                sx={{
                  color: "var(--palette-header_text, #fff)",
                  fontSize: "0.875rem",
                  fontFamily: "Faktum, sans-serif",
                  fontWeight: 400,
                  textAlign: "center",
                  lineHeight: "normal",
                }}
              >
                Make it effortless for leads and customers to book with Blazeo
                Appointments, unlock with Pro Plan.{" "}
                <Box
                  component="a"
                  onClick={() => setPackagesModalOpen(true)}
                  sx={{
                    color: "var(--palette-orange_shade)",
                    fontWeight: 500,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontFamily: "Faktum, sans-serif",
                  }}
                >
                  Upgrade Now
                </Box>
              </Typography>
            </>
          )}
        </Box>

        {/* RIGHT: Expiration chip + profile/icons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "18px",
            flexShrink: 0,
          }}
        >
          {/* Days Left Chip */}
          {daysLeft !== null && daysLeft <= 15 && daysLeft >= 0 && (
            <Tooltip
              title={`Your subscription plan will expire in ${daysLeft} days`}
              placement="bottom"
            >
              <Chip
                label={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Faktum, sans-serif",
                        fontWeight: 700,
                        fontSize: "1rem",
                        lineHeight: "normal",
                        color: "#fff",
                      }}
                    >
                      {daysLeft}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                    >
                      DAYS LEFT
                    </Typography>
                  </Box>
                }
                sx={{
                  backgroundColor: "var(--palette-orange_shade)",
                  borderRadius: "5px",
                  height: "auto",
                  px: "7px",
                  py: "3px",
                  "& .MuiChip-label": { px: 0 },
                }}
              />
            </Tooltip>
          )}

          {/* Existing right nav items (notifications, profile, settings etc) */}
          <RightNavSection handleTheme={handleTheme} />
        </Box>
      </Toolbar>
      <ThemeSidebar open={open} onClose={() => setOpen(false)} />
      <PackagesModal
        open={packagesModalOpen}
        onClose={() => setPackagesModalOpen(false)}
      />
    </AppBar>
  );
}
